import useAppDispatch from "../../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, FormInstance } from "antd";
import ConfirmPopup from "../../../../components/ConfirmPopup/ConfirmPopup";
import { useClassMutations } from "../../../../api/mutations";
import { openErrorMessage, openSuccessMessage } from "../../../../store/message/messageSlice";
import { handleError } from "../../../../helper/errorHandler";
import styles from "./ClassEditPopup.module.scss";
import FormItemInput from "../../../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import { IClassForTable, IClassUpdateModel } from "../../../../models/Class";
import FormItemInputNumber from "../../../../components/ui/FormItemInputNumber/FormItemInputNumber";
import euroSvg from "../../../../assets/images/icons/euro.svg";
import FormItemCheckbox from "../../../../components/ui/FormItemCheckbox/FormItemCheckbox";
import FormItemSelect from "../../../../components/ui/FormItemSelect/FormItemSelect";
import { useGetAllClassesCategoriesQuery } from "../../../../api/queries";
import { UNLIMITED_CODE_WORD, UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS } from "../../../../constants";
import { timeRanges } from "../../../../helper/timeRanges";

interface IClassEditPopupProps {
  closePopup: () => void;
  item: IClassForTable;
  getData: () => void;
  gymListingId: string;
  gymName?: string;
}

type ClassEditFormValues = {
  categoryId: string;
  enableClubPass: boolean;
  isOpenGymForClubPass: boolean;
  priceForClubPass: number;
  numberSlotsClubPass: string;
  openGymFromTime: string;
  openGymToTime: string;
  applyToAll?: boolean;
};

const ClassEditPopup: React.FC<IClassEditPopupProps> = ({
  closePopup,
  item,
  getData,
  gymListingId,
  gymName,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  // form values waiting for the "apply to all services" confirmation
  const [pendingValues, setPendingValues] = useState<ClassEditFormValues | null>(null);

  const { updateClass, isUpdatingClass } = useClassMutations();
  const { data: categories = [] } = useGetAllClassesCategoriesQuery(null);

  const categoriesForSelect = useMemo(() => {
    if (!categories.length) return [];
    return categories.map((el) => {
      return {
        id: el.id,
        label: el.name,
      };
    });
  }, [categories]);

  useEffect(() => {
    if (formRef.current && item) {
      formRef.current.setFieldsValue({
        enableClubPass: item.enableClubPass,
        isOpenGymForClubPass: item.isOpenGymForClubPass,
        priceForClubPass: item.priceForClubPass,
        numberSlotsClubPass:
          Number(item.numberSlotsClubPass) === UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS
            ? UNLIMITED_CODE_WORD
            : item.numberSlotsClubPass,
        categoryId: item.clubPassCategoryId,
        openGymFromTime: item.openGymFromTime,
        openGymToTime: item.openGymToTime,
      });
    }
  }, [item, formRef.current]);

  const save = (values: ClassEditFormValues) => {
    const applyToAll = !!values.applyToAll;
    const model: IClassUpdateModel = {
      trainingCourseId: item.id,
      categoryId: values.categoryId,
      enableClubPass: values.enableClubPass,
      isOpenGymForClubPass: values.isOpenGymForClubPass,
      priceForClubPass: values.priceForClubPass,
      numberSlotsClubPass:
        values.numberSlotsClubPass === UNLIMITED_CODE_WORD
          ? UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS
          : Number(values.numberSlotsClubPass),
      openGymFromTime: values.openGymFromTime,
      openGymToTime: values.openGymToTime,
      applyToAll,
    };

    updateClass(
      { id: gymListingId, model },
      {
        onSuccess: (result) => {
          if (result?.failed?.length) {
            dispatch(
              openErrorMessage(
                t("ServicesNotUpdated", {
                  updated: result.updated,
                  names: result.failed.map((el) => el.name).join(", "),
                })
              )
            );
          } else {
            dispatch(
              openSuccessMessage(
                applyToAll
                  ? t("AllServicesUpdatedSuccessfully", { count: result?.updated ?? 0 })
                  : t("ClassUpdatedSuccessfully")
              )
            );
          }
          closePopup();
          getData();
        },
        onError: (e) => {
          handleError(e, dispatch, t);
          // with applyToAll some services may already be saved before the error
          if (applyToAll) getData();
        },
      }
    );
  };

  const onFinish = (values: ClassEditFormValues) => {
    if (isUpdatingClass) return;
    if (!values.applyToAll) return save(values);
    setPendingValues(values);
  };

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <h3 className={styles.title}>
          {t("Class")}: <span>{item.name}</span>
        </h3>
        <Form
          className={styles.form}
          ref={formRef}
          name="create-category"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemCheckbox
            name={"enableClubPass"}
            label={t("EnableClubPassForThisClass")}
            rules={{ required: true }}
          />

          <FormItemCheckbox
            name={"isOpenGymForClubPass"}
            label={t("AvailableForOpenGym")}
            rules={{ required: true }}
          />

          <FormItemSelect
            name={"categoryId"}
            label={t("Category") + " *"}
            rules={{ required: true }}
            items={categoriesForSelect}
          />

          <FormItemInput
            name={"numberSlotsClubPass"}
            label={t("NumberOfSlotsForClubPass") + " *"}
            rules={{ required: true }}
            unlimitedBtnVisible={true}
            setUnlimitedFn={() =>
              formRef.current?.setFieldsValue({
                numberSlotsClubPass: UNLIMITED_CODE_WORD,
              })
            }
          />

          <FormItemInputNumber
            name={"priceForClubPass"}
            label={t("Price") + " *"}
            rules={{ required: true }}
            prefix={euroSvg}
          />

          <div className={styles.row}>
            <FormItemSelect
              name={"openGymFromTime"}
              label={t("OpenGymAvailableFrom") + " *"}
              rules={{ required: true }}
              items={timeRanges}
            />

            <FormItemSelect
              name={"openGymToTime"}
              label={t("OpenGymAvailableTo") + " *"}
              rules={{ required: true }}
              items={timeRanges}
            />
          </div>

          <FormItemCheckbox
            name={"applyToAll"}
            label={t("ApplyChangesToAllServices")}
            rules={{ required: false }}
          />

          <div className={styles.btnsWrapper}>
            <CustomButton
              text={t("Close")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={closePopup}
            />
            <CustomButton
              text={t("Save")}
              type={"submit"}
              typeStyle={"fill"}
              disabled={isUpdatingClass}
            />
          </div>
        </Form>
      </div>
      {pendingValues && (
        <ConfirmPopup
          title={t("ApplyToAllServicesConfirm", { gymName: gymName || "" })}
          onClickYes={() => {
            save(pendingValues);
            setPendingValues(null);
          }}
          onClickNo={() => setPendingValues(null)}
        />
      )}
    </div>
  );
};

export default ClassEditPopup;
